import { useState } from 'react'
import { useQueryState, parseAsString } from 'nuqs'
import { DataTablePaginate } from '@/common/ui/table/data-table-paginate'
import { DataTableFilterSelector } from '@/common/ui/table/data-table-filter-selector'
import { DataTableResetFilter } from '@/common/ui/table/data-table-reset-filter'
import { DataTableSimpleSearch } from '@/common/ui/table/data-table-simple-search'
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from '@/common/ui/alert-dialog'
import useMutationDeleteTournament from '@/modules/tournament/api/hooks/useMutationDeleteTournament'
import { TOURNAMENT_STATUS_OPTIONS } from '@/modules/tournament/constants'
import { createTournamentColumns } from './constants'
import type { components } from '@/schema'

type Tournament = components['schemas']['AdminTournamentResponse']

interface TournamentTableProps {
	data: Tournament[]
	total: number
	isLoading: boolean
}

export default function TournamentTable({
	data,
	total,
	isLoading,
}: TournamentTableProps) {
	const [deleteId, setDeleteId] = useState<string | null>(null)
	const deleteMutation = useMutationDeleteTournament()

	const [, setStatusFilter] = useQueryState(
		'status',
		parseAsString.withOptions({ shallow: false }).withDefault(''),
	)
	const [, setIsActiveFilter] = useQueryState(
		'isActive',
		parseAsString.withOptions({ shallow: false }).withDefault(''),
	)

	const handleDelete = async () => {
		if (deleteId) {
			await deleteMutation.mutateAsync(deleteId)
			setDeleteId(null)
		}
	}

	const columns = createTournamentColumns((id) => setDeleteId(id))

	return (
		<>
			<div className="space-y-4">
				<div className="flex flex-wrap items-center gap-4">
					<DataTableSimpleSearch placeHolder="ค้นหาชื่อทัวร์นาเมนต์..." />
					<DataTableFilterSelector
						options={TOURNAMENT_STATUS_OPTIONS}
						placeholder="เลือกสถานะ"
						label="สถานะ"
						onValueChange={(value) => setStatusFilter(value || null)}
					/>
					<DataTableFilterSelector
						options={[
							{ value: 'true', label: 'เปิดใช้งาน' },
							{ value: 'false', label: 'ปิดใช้งาน' },
						]}
						placeholder="สถานะการใช้งาน"
						label="การใช้งาน"
						onValueChange={(value) => setIsActiveFilter(value || null)}
					/>
					<DataTableResetFilter />
				</div>
				<DataTablePaginate
					columns={columns}
					data={data}
					totalItems={total}
					isLoading={isLoading}
				/>
			</div>

			<AlertDialog
				open={!!deleteId}
				onOpenChange={(open) => !open && setDeleteId(null)}
			>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>ยืนยันการลบทัวร์นาเมนต์</AlertDialogTitle>
						<AlertDialogDescription>
							คุณแน่ใจหรือไม่ที่จะลบทัวร์นาเมนต์นี้?
							การดำเนินการนี้ไม่สามารถย้อนกลับได้
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>ยกเลิก</AlertDialogCancel>
						<AlertDialogAction
							onClick={handleDelete}
							className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
						>
							ลบ
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</>
	)
}
